"use client";
import SearchIcon from "@/assets/icons/search.svg";
import { FacetItem, PopularBrandItem } from "@/common/lib/types";
import Checkbox from "@/components/ui/checkbox/Checkbox";
import TextField from "@/components/ui/textField/TextField";
import { debounce } from "@/utils/common";
import Image from "next/image";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import PopularBrands from "./PopularBrands";
import { cn } from "@/lib/tailwind";

export interface BrandListProps {
  facet: FacetItem[];
  selectedValues: string[];
  onClick: (value: string) => void;
  translations: { singleItem: string; multipleItem: string; searchText: string };
  col?: number;
  popularBrands?: { title: string; brands: PopularBrandItem[] };
}

const BrandList = ({
  col = 1,
  facet,
  selectedValues,
  popularBrands,
  translations,
  onClick,
}: BrandListProps) => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredBrands, setFilteredBrands] = useState<FacetItem[]>(facet);

  const debouncedSearch = useCallback(
    debounce((term: string) => {
      const results = facet?.filter((brand) =>
        brand?.label?.toLowerCase().includes(term?.toLowerCase()),
      );
      setFilteredBrands(results);
    }, 300),
    [],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e?.target?.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  const handleChange = (value: string) => {
    onClick(value);
  };

  const sortedBrands = useMemo(() => {
    const selectedBrands = filteredBrands
      ?.filter((item) => selectedValues?.includes(item.value))
      .sort((a, b) => (a.value ?? "").localeCompare(b.value ?? "", "en")); // ← A→Z

    const unselectedBrands = filteredBrands
      ?.filter((item) => !selectedValues?.includes(item.value))
      .sort((a, b) => (a.value ?? "").localeCompare(b.value ?? "", "en")); // ← A→Z

    return [...selectedBrands, ...unselectedBrands];
  }, [filteredBrands, selectedValues]);

  useEffect(() => {
    setFilteredBrands(facet);
  }, [facet]);

  return (
    <div className={cn(`grid p-4`, popularBrands ? "grid-cols-2" : "grid-cols-1")}>
      {popularBrands && (
        <div className={`w-full ${col === 1 ? "col-span-2" : "col-span-1 border-r-[1px]"}`}>
          <PopularBrands
            col={col}
            popularBrands={popularBrands}
            selectedValues={selectedValues}
            filteredBrands={filteredBrands}
            handleChange={handleChange}
          />
        </div>
      )}
      <div className={` ${col === 1 ? "col-span-2" : "col-span-1 pl-4"}`}>
        <TextField
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder={translations?.searchText}
          slots={{
            startAction: {
              action: <Image src={SearchIcon?.src} alt="Search" width={19} height={19} />,
            },
          }}
        />
        <div>
          {sortedBrands?.map((item, index) => (
            <React.Fragment key={index}>
              <div
                className="flex cursor-pointer items-center justify-between py-4"
                onClick={() => handleChange(item?.value)}
              >
                <p className="text-sm">{item?.label || ""}</p>
                <div className="flex items-center">
                  <p className="me-2 text-xs text-tertiary-6">
                    {item?.count > 1
                      ? translations?.multipleItem?.replace("{{count}}", `${item?.count}`)
                      : translations?.singleItem?.replace("{{count}}", `${item?.count}`)}
                  </p>
                  <Checkbox
                    checked={selectedValues?.includes(item?.value)}
                    onChange={() => {}}
                    className="h-4 w-4 rounded-[3px] border-tertiary-4 checked:bg-[#4285F4] hover:bg-white hover:checked:bg-[#4285F4] focus:checked:bg-[#4285F4]"
                  />
                </div>
              </div>
              <div className="h-[1px] bg-tertiary-7" />
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BrandList;
